import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { MdMenu } from 'react-icons/md';
import ReceptionistSidebar from '../components/receptionist/Sidebar';
import { PanelToastProvider } from '../context/PanelToastContext';
import { GymSettingsProvider, useGymSettings } from '../context/GymSettingsContext';
import { useAuth } from '../context/AuthContext';
import { syncReceptionistBranchFromProfile } from '../services/receptionist/receptionistApi';
import { setCurrentReceptionistId } from '../services/receptionist/receptionistContext';
import { warmPanelData } from '../services/panelBootstrap';
import { setActiveGymId } from '../services/receptionist/gymContext';
import {
  getReceptionistBranch,
  RECEPTIONIST_BRANCH_EVENT,
  setPanelDataScope,
} from '../services/gym/branchContext';
import OfflineStatusBanner from '../components/shared/OfflineStatusBanner';
import './OwnerLayout.css';
import '../pages/receptionist/receptionistPanel.css';

function ReceptionistShell() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [branchLabel, setBranchLabel] = useState(() => getReceptionistBranch());
  const { account } = useAuth();
  const { gym } = useGymSettings();

  useEffect(() => {
    setPanelDataScope('receptionist');
    return () => setPanelDataScope('');
  }, []);

  useEffect(() => {
    if (!account?.gym_id) return undefined;

    let cancelled = false;

    async function bootstrap() {
      setActiveGymId(account.gym_id);
      if (account.staff_id) setCurrentReceptionistId(account.staff_id);
      try {
        await syncReceptionistBranchFromProfile();
      } catch {
        // Pages handle missing branch themselves.
      }
      if (cancelled) return;
      setBranchLabel(getReceptionistBranch());
      // Light warm once after branch is known (no force — avoid repeat full work)
      warmPanelData(account.gym_id, { role: 'receptionist' });
    }

    bootstrap();
    return () => { cancelled = true; };
  }, [account?.gym_id, account?.staff_id]);

  useEffect(() => {
    const onBranch = (event) => {
      const next = event?.detail?.branch || getReceptionistBranch();
      setBranchLabel((prev) => (prev === next ? prev : next));
      // Do not re-warm on every branch event — pages refetch themselves.
    };
    window.addEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
    return () => window.removeEventListener(RECEPTIONIST_BRANCH_EVENT, onBranch);
  }, [account?.gym_id]);

  return (
    <div className="owner-layout">
      <ReceptionistSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="owner-main">
        <OfflineStatusBanner />
        <header className="owner-topbar">
          <button
            type="button"
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open menu"
          >
            <MdMenu />
          </button>
          <span className="owner-topbar-label">
            Receptionist Panel
            {gym?.name ? ` · ${gym.name}` : ''}
            {branchLabel ? ` · ${branchLabel}` : ''}
          </span>
        </header>
        <main className="owner-content">
          {/* Stable key — remounting on branch change caused the members table to flash away */}
          <Outlet key={account?.gym_id || 'receptionist'} />
        </main>
      </div>
    </div>
  );
}

function ReceptionistLayout() {
  return (
    <PanelToastProvider>
      <GymSettingsProvider>
        <ReceptionistShell />
      </GymSettingsProvider>
    </PanelToastProvider>
  );
}

export default ReceptionistLayout;
